import {
  DEFAULT_BOOKING_WINDOW_DAYS,
  MIN_BOOKING_WINDOW_DAYS,
  MAX_BOOKING_WINDOW_DAYS,
  DEFAULT_NEXT_DAY_GRID_END_HOUR,
  MAX_NEXT_DAY_GRID_END_HOUR,
  MAX_CELL_INITIALS_LENGTH,
  COURT_CHIP_COLORS,
  COURT_CHIP_NEUTRAL,
  ADMIN_GRID_STATUS_OPTIONS,
  CLOSE_REASON_PROMPT_CONFIG_KEY,
} from '../constants/bookingConst';
// One-way: promoUtils imports nothing from here, so getCourtRate can call
// into it without the two forming a cycle.
import { applyPromoToRate } from './promoUtils';
import { formatMoney } from './currencyUtil';

// Determines whether a time-slot label (e.g. "6-7PM") falls at or after
// the configured peak-rate start hour, so callers can pick the peak vs.
// off-peak price for it.
export const isPeakTime = (timeSlot, peakRateStart) => {
  // Example: "11-12PM" -> ["11", "12PM"]
  const [startStr, endStr] = timeSlot.split('-');
  let hour = parseInt(startStr, 10);
  
  const upperEnd = endStr.toUpperCase();
  const upperStart = startStr.toUpperCase();

  // Determine if the start time is AM or PM
  let isStartPm = false;

  if (upperStart.includes("PM")) {
    isStartPm = true;
  } else if (upperStart.includes("AM")) {
    isStartPm = false;
  } else {
    // If the start hour lacks an explicit AM/PM, deduce it from the end string
    if (upperEnd.includes("PM")) {
      // "11-12PM" starts at 11 AM (AM to PM transition)
      // "1-2PM" or "6-7PM" starts at PM
      isStartPm = (hour !== 11);
    } else if (upperEnd.includes("AM")) {
      // "11-12AM" starts at 11 PM (PM to AM transition)
      // "6-7AM" starts at AM
      isStartPm = (hour === 11);
    }
  }

  // Standard 12-hour to 24-hour conversion
  if (isStartPm && hour !== 12) {
    hour += 12; // 1PM-11PM becomes 13-23
  } else if (!isStartPm && hour === 12) {
    hour = 0;  // 12AM becomes 0
  }

  // Peak is 6 PM (18:00) and later
  return hour >= peakRateStart;
};

// Parses a time-slot label like "6-7AM", "11-12PM", "11-12AM" into its
// real 24-hour clock START hour (0-23).
//
// Uses the standard 12-hour scheduling convention: the trailing AM/PM
// suffix describes the END hour. For every start hour except 11, the
// start hour shares the same meridiem as the suffix (no boundary crossed).
// 11 is the one hour that crosses the noon/midnight boundary, so its
// meridiem is the OPPOSITE of the suffix ("11-12PM" = 11 AM -> 12 PM noon,
// "11-12AM" = 11 PM -> 12 AM midnight).
export const parseTimeSlotStartHour = (timeSlot) => {
  if (!timeSlot || typeof timeSlot !== 'string') return 0;
  const match = timeSlot.match(/^(\d{1,2})\s*-\s*\d{1,2}\s*(AM|PM)/i);
  if (!match) {
    const n = timeSlot.match(/\d+/);
    return n ? parseInt(n[0], 10) % 24 : 0;
  }
  const h1 = parseInt(match[1], 10);
  const suffix = match[2].toUpperCase();
  const isPm = h1 === 11 ? suffix === 'AM' : suffix === 'PM';
  if (h1 === 12) return isPm ? 12 : 0;
  return isPm ? h1 + 12 : h1;
};

// Returns a copy of timeSlots sorted in chronological (start-hour) order.
export const sortTimeSlots = (timeSlots = []) => {
  return [...(timeSlots || [])].sort((a, b) => parseTimeSlotStartHour(a) - parseTimeSlotStartHour(b));
};

// Formats a 24-hour hour number as a display label, e.g. 6 -> "6:00 AM",
// 12 -> "12:00 NN" (noon), 0 -> "12:00 MN" (midnight).
export const formatHourLabel = (hour24) => {
  const h = ((hour24 % 24) + 24) % 24;
  if (h === 0) return '12:00 MN';
  if (h === 12) return '12:00 NN';
  return h < 12 ? `${h}:00 AM` : `${h - 12}:00 PM`;
};

/**
 * Collapses a court's hours into ranges: four consecutive one-hour slots
 * render as "5:00 AM - 9:00 AM" rather than four comma-separated pairs.
 *
 * Listing every slot individually made a long booking unreadable — an
 * all-day or overnight one produced a wall of near-identical fragments that
 * had to be scanned end to end just to work out when it started and ended.
 * A gap in the hours still splits the run, so "9-10AM, 2-3PM" stays two
 * ranges and is never silently smoothed into one continuous block.
 *
 * @param {string[]} slots - raw slot labels, e.g. ["5-6AM", "6-7AM"]
 * @returns {string}
 */
export const formatSlotRanges = (slots) => {
  const sorted = sortTimeSlots(slots || []);
  if (sorted.length === 0) return '';
  const hours = sorted.map(parseTimeSlotStartHour);

  const runs = [];
  let start = hours[0];
  let prev = hours[0];
  for (let i = 1; i < hours.length; i += 1) {
    // Each slot is one hour, so a run continues while each start is the
    // previous one plus one. Duplicates (same hour twice) are absorbed
    // rather than breaking the run.
    if (hours[i] === prev + 1 || hours[i] === prev) {
      prev = hours[i];
    } else {
      runs.push([start, prev]);
      start = hours[i];
      prev = hours[i];
    }
  }
  runs.push([start, prev]);

  // The end of a run is the END of its last slot, hence +1; % 24 so a run
  // finishing at 11PM reads "12:00 MN" rather than a 24th hour.
  return runs
    .map(([from, to]) => `${formatHourLabel(from)} - ${formatHourLabel((to + 1) % 24)}`)
    .join(', ');
};

// Formats a raw slot label ("10-11PM", "11-12AM") as a display range like
// "10:00-11:00 PM" / "11:00-12:00 MN", matching the jassen reference design
// (MN = midnight, NN = noon). Used anywhere a slot needs to read as a
// human-friendly time range rather than the raw config label.
const hourAndMeridiem = (hour24) => {
  const h = ((hour24 % 24) + 24) % 24;
  if (h === 0) return { h: 12, m: 'MN' };
  if (h === 12) return { h: 12, m: 'NN' };
  return h < 12 ? { h, m: 'AM' } : { h: h - 12, m: 'PM' };
};

/**
 * A slot's range split into its parts, so a caller can set the AM/PM/NN/MN
 * suffixes in their own type.
 *
 * Both ends are named only when they DISAGREE. "11:00-12:00 MN" reads as
 * eleven in the morning to noon unless you already know the convention — it
 * is actually 11PM to midnight. Four slots a day cross a boundary like that
 * (MN->AM, AM->NN, NN->PM, PM->MN); the other twenty share one suffix and
 * are clearer with it stated once at the end.
 *
 * @param {string} timeSlot
 * @returns {{start: string, startMeridiem: string, end: string, endMeridiem: string, sameMeridiem: boolean}}
 */
export const formatTimeSlotRangeParts = (timeSlot) => {
  const startHour = parseTimeSlotStartHour(timeSlot);
  const endHour = (startHour + 1) % 24;
  const start = hourAndMeridiem(startHour);
  const end = hourAndMeridiem(endHour);
  return {
    start: `${start.h}:00`,
    startMeridiem: start.m,
    end: `${end.h}:00`,
    endMeridiem: end.m,
    sameMeridiem: start.m === end.m,
  };
};

// Built from the parts above so the two can never drift apart. A slot that
// stays within one half of the day keeps the long-standing "6:00-7:00 AM"
// shape; only the four boundary-crossing slots gain the second suffix.
export const formatTimeSlotRange = (timeSlot) => {
  const p = formatTimeSlotRangeParts(timeSlot);
  return p.sameMeridiem
    ? `${p.start}-${p.end} ${p.endMeridiem}`
    : `${p.start}${p.startMeridiem}-${p.end}${p.endMeridiem}`;
};

// MN/NN collapse into the ordinary AM/PM pair here: midnight IS 12:00 AM and
// noon IS 12:00 PM, and saying it that way stays unambiguous precisely BECAUSE
// both ends get a suffix whenever they disagree — "11:00 PM - 12:00 AM" cannot
// be misread the way a bare "11:00-12:00 MN" could.
const PLAIN_MERIDIEM = { AM: 'AM', PM: 'PM', MN: 'AM', NN: 'PM' };

/**
 * A slot's range in the guest summary panel's style: "5:00 - 6:00 PM", with
 * the suffix named once at the end when both ends share it and on both ends
 * when they differ ("11:00 AM - 12:00 PM").
 *
 * Separate from formatTimeSlotRange above rather than replacing it: that one
 * is used in two dozen places including the PDF receipt and the admin screens,
 * which keep the established "6:00-7:00 AM" shape — no spaces around the dash,
 * and MN/NN preserved.
 *
 * @param {string} timeSlot - a config time-slot label.
 * @returns {string}
 */
export const formatTimeSlotRangeMeridiem = (timeSlot) => {
  const p = formatTimeSlotRangeParts(timeSlot);
  const startSuffix = PLAIN_MERIDIEM[p.startMeridiem] || '';
  const endSuffix = PLAIN_MERIDIEM[p.endMeridiem] || '';
  return startSuffix === endSuffix
    ? `${p.start} - ${p.end} ${endSuffix}`
    : `${p.start} ${startSuffix} - ${p.end} ${endSuffix}`;
};

/**
 * Collapses a court's booked hours into as few readable spans as possible:
 * ["7-8PM", "8-9PM", "9-10PM"] becomes "7:00 - 10:00 PM" rather than three
 * separate lines. A gap in the booking starts a new span, so a guest who booked
 * 7-8PM and 10-11PM still sees both ("7:00 - 8:00 PM, 10:00 - 11:00 PM")
 * instead of one span that wrongly implies four unbroken hours.
 *
 * Shares formatTimeSlotRangeParts and PLAIN_MERIDIEM with
 * formatTimeSlotRangeMeridiem above so a span of one hour renders identically
 * to that single slot.
 *
 * @param {string[]} timeSlots - Raw slot keys, e.g. ["7-8PM", "8-9PM"].
 * @returns {string} Comma-separated spans, or '' when given nothing.
 */
export const formatTimeSlotSpan = (timeSlots = []) => {
  const sorted = sortTimeSlots(timeSlots).filter(Boolean);
  if (sorted.length === 0) return '';

  // Group into runs of consecutive hours. The % 24 keeps a run intact across
  // midnight, where 11PM (23) is followed by 12AM (0), not by 24.
  const runs = [];
  sorted.forEach(slot => {
    const currentRun = runs[runs.length - 1];
    const previousSlot = currentRun && currentRun[currentRun.length - 1];
    const isConsecutive = previousSlot
      && (parseTimeSlotStartHour(previousSlot) + 1) % 24 === parseTimeSlotStartHour(slot);
    if (isConsecutive) currentRun.push(slot);
    else runs.push([slot]);
  });

  return runs.map(run => {
    const first = formatTimeSlotRangeParts(run[0]);
    const last = formatTimeSlotRangeParts(run[run.length - 1]);
    const startSuffix = PLAIN_MERIDIEM[first.startMeridiem] || '';
    const endSuffix = PLAIN_MERIDIEM[last.endMeridiem] || '';
    return startSuffix === endSuffix
      ? `${first.start} - ${last.end} ${endSuffix}`
      : `${first.start} ${startSuffix} - ${last.end} ${endSuffix}`;
  }).join(', ');
};

/**
 * Re-orders a day's slots so the list BEGINS at `startHour`, wrapping the
 * hours before it round to the end — 6AM first, with midnight-to-5AM landing
 * after 11PM rather than at the top.
 *
 * Deliberately NOT a change to sortTimeSlots: that is the app's chronological
 * comparator, and roughly twenty call sites depend on it to find a booking's
 * earliest and latest hour, build ranges and group slots. This only changes
 * the order rows are DISPLAYED in.
 *
 * @param {string[]} timeSlots
 * @param {number} startHour - 0-23, the hour the displayed day opens on
 * @returns {string[]}
 */
export const orderSlotsFromDayStart = (timeSlots, startHour) => {
  const anchor = ((Math.floor(Number(startHour)) % 24) + 24) % 24 || 0;
  return sortTimeSlots(timeSlots).slice().sort((a, b) => (
    ((parseTimeSlotStartHour(a) - anchor + 24) % 24) -
    ((parseTimeSlotStartHour(b) - anchor + 24) % 24)
  ));
};

// A time slot is "passed" once its start hour is at or before the current
// hour, but only for the currently-viewed date being today (PH time).
export const isSlotPassed = (isToday, timeSlot, currentHour) => {
  if (!isToday) return false;
  return parseTimeSlotStartHour(timeSlot) <= currentHour;
};

/**
 * Whether a slot is completely over — its END time has gone by — which is
 * when the guest booking grid marks it Passed. A 4-5PM slot stays open until
 * 5:00 PM and reads Passed from 5:01 PM; 11PM-12MN from 12:01 AM the next day.
 *
 * Unlike isSlotPassed (start-hour based, still used by the admin screens),
 * this works across dates, because a slot ending at midnight only ends on the
 * NEXT date: the slot's end is measured in minutes from the start of `todayStr`.
 *
 * @param {string} slotDateStr - 'yyyy-MM-dd' the slot belongs to
 * @param {string} timeSlot - e.g. '4-5PM'
 * @param {string} todayStr - today in PH time, 'yyyy-MM-dd'
 * @param {number} nowMinutes - minutes since PH midnight (0-1439)
 * @returns {boolean} false when either date can't be parsed
 */
export const hasSlotEnded = (slotDateStr, timeSlot, todayStr, nowMinutes) => {
  const toUtcDay = (str) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(str || ''));
    return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 86400000 : null;
  };
  const slotDay = toUtcDay(slotDateStr);
  const today = toUtcDay(todayStr);
  if (slotDay === null || today === null) return false;
  const endMinutes = (slotDay - today) * 1440 + (parseTimeSlotStartHour(timeSlot) + 1) * 60;
  // Strictly after the end minute: at exactly 5:00 PM the 4-5PM slot is still
  // on, from 5:01 PM it is Passed.
  return nowMinutes > endMinutes;
};

/**
 * Today in PH time as "yyyy-MM-dd".
 *
 * Read straight from Intl in the Asia/Manila zone rather than going through
 * getPhToday()+formatInPhTime(): that pair round-trips a Date through a
 * local-midnight parse and a manual UTC+8 shift, which is correct but
 * fragile, and every comparison here is against a stored "yyyy-MM-dd" where
 * a lexicographic compare is already a valid date compare.
 *
 * @returns {string}
 */
export const phTodayDateStr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Manila' });

/**
 * Whether every date a booking covers is already behind us.
 *
 * Used to freeze a finished reservation: confirming, declining or cancelling
 * one rewrites the record of something that already happened, and releases
 * court hours nobody can rebook. See canActOnBooking in AdminBooking.jsx for
 * who is allowed past it (the owner account, or everyone when "View past
 * dates" is on).
 *
 * The LATEST date decides, not the first. A multi-date reservation running
 * Fri-Sun is still live on Saturday, and cancelling it then legitimately
 * calls off the Sunday — so it freezes only once the whole thing is over.
 *
 * A booking with no readable date is treated as NOT past: refusing to act on
 * a record whose date can't be read would strand it with no way to resolve
 * it at all.
 *
 * @param {object} booking - a raw record (`date`) or a grouped entry (`dates`)
 * @param {string} [todayStr] - "yyyy-MM-dd"; defaults to today in PH time
 * @returns {boolean}
 */
export const isBookingFullyPast = (booking, todayStr = phTodayDateStr()) => {
  if (!booking) return false;
  const dates = [
    ...(Array.isArray(booking.dates) ? booking.dates.map(d => d?.date) : []),
    booking.date,
    booking.originalData?.date,
    ...(Array.isArray(booking.originalData?.dates) ? booking.originalData.dates.map(d => d?.date) : []),
  ].filter(d => typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d));
  if (dates.length === 0) return false;
  return dates.every(d => d < todayStr);
};

/**
 * Which statuses the "Manage availability" grid dropdown offers, from
 * Config -> System -> "Availability status options".
 *
 * Stored as a { [status]: boolean } map rather than an array: Firebase drops
 * an empty array entirely, which would silently bring every default back the
 * moment an admin unticked the last box. A status missing from the map
 * (never saved, or added in a later build) falls back to its defaultEnabled.
 *
 * @param {object} config - app config
 * @returns {Set<string>} the enabled status values
 */
export const getEnabledGridStatuses = (config) => {
  const saved = config?.adminGridStatusOptions || {};
  return new Set(ADMIN_GRID_STATUS_OPTIONS
    .filter(opt => (typeof saved[opt.value] === 'boolean' ? saved[opt.value] : opt.defaultEnabled))
    .map(opt => opt.value));
};

/**
 * Display name for a court — the custom name an admin gave it in
 * Config -> Sports & Courts, falling back to "Court {number}" when that
 * field is blank (which it is for every court until an admin fills it in,
 * and for any config saved before the field existed).
 * @param {object} config - app config
 * @param {number|string} courtNumber
 * @returns {string}
 */
export const getCourtLabel = (config, courtNumber) => {
  const entry = (config?.courts || []).find(c => Number(c.number) === Number(courtNumber));
  const name = (entry?.name || '').trim();
  return name || `Court ${courtNumber}`;
};

/**
 * The identity colour for one court's chip (see COURT_CHIP_COLORS).
 *
 * Keyed on the court NUMBER, not on its position in a filtered list, so a
 * court keeps the same colour whichever sport or date is being viewed — a
 * colour that moved between courts when the list changed would be worse than
 * no colour at all.
 *
 * @param {number|string} courtNumber
 * @returns {{tint: string, accent: string}}
 */
export const getCourtChipColor = (courtNumber) => {
  const index = Math.floor(Number(courtNumber)) - 1;
  if (!Number.isFinite(index) || index < 0 || index >= COURT_CHIP_COLORS.length) {
    return COURT_CHIP_NEUTRAL;
  }
  return COURT_CHIP_COLORS[index];
};

/**
 * Splits a court label into its name and a trailing parenthetical note —
 * "Ball Machine (Pusun)" -> { name: 'Ball Machine', note: '(Pusun)' }.
 *
 * Clubs use the brackets for a secondary detail (a local nickname, a
 * sponsor), which reads as part of the name itself when it's set in the same
 * bold heading type. Splitting it lets a caller de-emphasise that half.
 *
 * Only a parenthetical that CLOSES the label counts, and only with a name in
 * front of it — "(Annex) Court 2" and a bare "(Pusun)" are left whole rather
 * than having their only visible text shrunk away.
 *
 * @param {string} label - typically getCourtLabel's output
 * @returns {{name: string, note: string}} note is '' when there is none
 */
export const splitCourtLabelNote = (label) => {
  const text = String(label || '').trim();
  const match = text.match(/^(.*\S)\s*(\([^()]*\))$/);
  if (!match) return { name: text, note: '' };
  return { name: match[1], note: match[2] };
};

/**
 * How many days ahead of today guests may book (Config -> System).
 *
 * Clamped rather than trusted: the config is a plain Firebase record, so a
 * blank, zero, negative or absurd value has to resolve to something the
 * booking screen can actually render. A config saved before this setting
 * existed has no key at all and falls back to the default, which is what
 * every club got implicitly before the limit was configurable.
 *
 * @param {object} config - app config
 * @returns {number} whole days, between MIN_ and MAX_BOOKING_WINDOW_DAYS
 */
export const getBookingWindowDays = (config) => {
  const raw = Math.floor(Number(config?.bookingWindowDays));
  if (!Number.isFinite(raw) || raw < MIN_BOOKING_WINDOW_DAYS) return DEFAULT_BOOKING_WINDOW_DAYS;
  return Math.min(raw, MAX_BOOKING_WINDOW_DAYS);
};

// ---- Court rules & policies ----
// Policies are an admin-managed list of { id, title, description } cards
// (Config -> Policies), the same shape as courtDetails. They replaced five
// fixed fields — ruleFoodDrinks / rulePets / ruleAttire / ruleSmoking /
// ruleCancellation — which are still read as a fallback so a config saved
// before the change keeps showing its rules until the migration runs.
const LEGACY_POLICY_FIELDS = [
  { key: 'ruleFoodDrinks', id: 'policy-food', title: 'Food & Drinks' },
  { key: 'rulePets', id: 'policy-pets', title: 'Pets' },
  { key: 'ruleAttire', id: 'policy-attire', title: 'Attire' },
  { key: 'ruleSmoking', id: 'policy-smoking', title: 'Smoking' },
  { key: 'ruleCancellation', id: 'policy-cancellation', title: 'Cancellation Policy' },
];

/**
 * The club's policy cards, skipping any an admin left completely blank so a
 * half-finished row never renders to guests.
 * @param {object} config
 * @returns {Array<{id: string, title: string, description: string}>}
 */
export const getPolicies = (config) => {
  const cards = (config?.policies || []).filter(
    p => (p?.title || '').trim() || (p?.description || '').trim()
  );
  if (cards.length > 0) return cards;
  return LEGACY_POLICY_FIELDS
    .filter(f => (config?.[f.key] || '').trim())
    .map(f => ({ id: f.id, title: f.title, description: config[f.key] }));
};

/**
 * The first policy whose title matches — used where one specific rule is
 * quoted rather than the whole list (the cancellation and attire FAQs, and
 * the landing page's cancellation line). Title-matched rather than
 * id-matched so a card the admin renamed or re-added still resolves.
 * @param {object} config
 * @param {RegExp} pattern
 * @returns {string} the description, or '' when nothing matches
 */
export const findPolicyText = (config, pattern) => {
  const hit = getPolicies(config).find(p => pattern.test(p.title || ''));
  return hit?.description || '';
};

/**
 * The landing page's auto-generated FAQ list — questions whose answers are
 * derived from config (address, court counts, rates, cancellation policy)
 * so they stay correct as the club's settings change.
 *
 * Used both as the live fallback when config.faqs is empty (LandingPage)
 * and as the seed for "Load default questions" in the admin (AdminConfig),
 * which is why it lives here rather than inside either component.
 *
 * @param {object} config
 * @returns {Array<{id: string, question: string, answer: string}>}
 */
export const buildDefaultFaqs = (config) => {
  const courtName = config?.courtName || 'our club';
  const sportsList = config?.sports?.length ? config.sports : [{ id: 'default', name: 'Pickleball' }];
  const multiSport = sportsList.length > 1;
  const courtsBySport = (sportId) => (config?.courts || []).filter(c => c.sportId === sportId);
  const totalCourts = config?.courts?.length || config?.numberOfCourts || 0;

  const courtsAnswer = multiSport
    ? sportsList.map(s => `${courtsBySport(s.id).length} ${s.name}`).filter(part => !part.startsWith('0 ')).join(' and ') + ' court(s)'
    : `${totalCourts ? `${totalCourts} ${sportsList[0]?.name || 'pickleball'} courts` : `Multiple ${sportsList[0]?.name || 'pickleball'} courts`}.`;

  // Reads the sport's rate bands so a club with three or more rates gets an
  // accurate answer; getSportRateBands falls back to the legacy two-rate
  // fields, so a config that predates bands reads exactly as it used to.
  const describeBands = (sportId) => getSportRateBands(config, sportId)
    .map(b => `${formatMoney(b.price, config)}/hour ${b.name ? `(${b.name}) ` : ''}${formatHourLabel(Number(b.startHour) || 0)}–${formatHourLabel(Number(b.endHour) || 0)}`)
    .join(', ');
  // "per reservation" is a promise the club can no longer keep once the fee
  // is charged per hour, so the sentence follows the setting.
  // Event packages are priced all-in, so the fee sentence would be a promise
  // the club doesn't keep for them — name the exception when any is offered.
  const hasPackages = Array.isArray(config?.packages) && config.packages.some(p => p?.active !== false);
  const packageExemption = hasPackages ? ' Event packages are all-in and carry no processing fee.' : '';
  const feeNote = config?.bookingFee
    ? (config?.bookingFeePerHour === true
      ? ` A ${formatMoney(config.bookingFee, config)} processing fee applies per court, per hour booked.${packageExemption}`
      : ` A ${formatMoney(config.bookingFee, config)} processing fee applies per reservation.${packageExemption}`)
    : '';
  const singleSportBands = describeBands(sportsList[0]?.id);

  const ratesAnswer = multiSport
    ? sportsList.map(s => `${s.name}: ${describeBands(s.id) || 'see the Rates section above'}.`).join(' ') + feeNote
    : (singleSportBands
      ? `${singleSportBands}.${feeNote}`
      : 'Please check the Rates section above, or contact us directly.');

  return [
    { id: 'faq-location', question: `Where is ${courtName} located?`, answer: config?.locationText || `Contact us at ${config?.contactNumber || 'our listed number'} for directions.` },
    { id: 'faq-courts', question: 'How many courts do you have?', answer: courtsAnswer },
    { id: 'faq-rates', question: 'What are your rates?', answer: ratesAnswer },
    { id: 'faq-booking', question: 'How do I book a court?', answer: 'Pick a date and time slot online, fill in your details, then send your payment and upload the receipt to reserve your slot.' },
    { id: 'faq-payment', question: 'Do I need to pay before my booking is confirmed?', answer: `Yes — after selecting your slot you'll have ${config?.paymentTimeoutMinutes || 5} minute(s) to upload your payment receipt. We confirm your booking once it's been reviewed.` },
    { id: 'faq-cancel', question: 'Can I cancel or reschedule?', answer: findPolicyText(config, /cancel/i) || 'Please contact us directly to arrange a cancellation or reschedule.' },
    { id: 'faq-openplay', question: 'What is open play and how does it work?', answer: 'Open play sessions let you pay per player and jump into friendly games without booking a whole court — ask our staff for the current schedule.' },
    { id: 'faq-attire', question: 'What should I wear?', answer: findPolicyText(config, /attire|wear|dress/i) || 'Comfortable athletic wear and non-marking court shoes are recommended.' },
    { id: 'faq-parking', question: 'Is parking available?', answer: `Reach out to us at ${config?.contactNumber || 'our contact number'} for parking details at our location.` },
  ];
};

// ---- Booking reference numbers ----

// Client-specific prefix for confirmation ids, e.g. "DPTC-08132026-XS9WLRQ5".
// MUST be read from a REACT_APP_-prefixed variable: Create React App only
// inlines those into the browser bundle, so a bare CLIENT_CONFIRMATION_PREFIX
// would silently be undefined at runtime. The unprefixed name is still
// checked as a fallback in case a build tool exposes it, and 'PB' keeps the
// old format working if neither is set.
const CONFIRMATION_PREFIX = (
  process.env.REACT_APP_CLIENT_CONFIRMATION_PREFIX
  || process.env.REACT_APP_CLIENT_CONFIMATION_PREFIX // tolerate the .env's existing spelling
  || 'PB'
).trim();

/**
 * "MMDDYYYY" for today in PH time. Read straight from Intl in Asia/Manila
 * so a guest booking from another timezone still gets the club's date on
 * their reference.
 */
const confirmationDatePart = () => {
  const [y, m, d] = new Date()
    .toLocaleDateString('en-CA', { timeZone: 'Asia/Manila' })
    .split('-');
  return `${m}${d}${y}`;
};

/**
 * 8 uppercase alphanumeric characters. Uses crypto.getRandomValues where
 * available (every current browser) so two guests checking out in the same
 * millisecond can't collide, falling back to Math.random otherwise.
 */
const confirmationRandomPart = (length = 8) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const cryptoObj = typeof window !== 'undefined' ? window.crypto : undefined;
  if (cryptoObj?.getRandomValues) {
    const bytes = new Uint8Array(length);
    cryptoObj.getRandomValues(bytes);
    return Array.from(bytes, b => alphabet[b % alphabet.length]).join('');
  }
  return Array.from({ length }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join('');
};

/**
 * Booking reference shown to guests, e.g. "DPTC-08132026-XS9WLRQ5" —
 * [client prefix]-[MMDDYYYY]-[8 random alphanumeric].
 * @returns {string}
 */
export const generateConfirmationId = () =>
  `${CONFIRMATION_PREFIX}-${confirmationDatePart()}-${confirmationRandomPart()}`;

/**
 * The reference code a club appends to a guest's name, e.g.
 * "Bryan Namoc - RIP-20260814-N9UJB" -> "RIP-20260814-N9UJB".
 *
 * Some clubs tag a booking with a code from another system by typing it after
 * the guest's name, and guests then look the booking up by that code alone.
 * Realtime Database can't do that in a query — it matches a whole value or a
 * prefix, never the tail of a string — so the code is pulled out of
 * `userName` here and compared in the browser instead (see
 * BookingStatusScreen's code search, which bounds what it reads by date
 * first).
 *
 * Matches a trailing <letters>-<6+ digits>-<3+ alphanumerics> token, either
 * after a dash separator or as the entire value. Deliberately narrow — a
 * surname or a hyphenated name must not be mistaken for a code.
 *
 * @param {string} userName
 * @returns {string|null} the code, or null when there isn't one
 */
export const extractGuestCode = (userName) => {
  const value = String(userName || '').trim();
  if (!value) return null;
  // Accepts "Name - CODE", "Name -CODE", "Name — CODE" (en/em dash) and a
  // bare "CODE".
  const match = /(?:^|[\s–—-]\s*)([A-Za-z][A-Za-z0-9]*-\d{6,}-[A-Za-z0-9]{3,})\s*$/.exec(value);
  return match ? match[1] : null;
};

// ---- Google Maps helpers ----
// One admin field (Config -> Basic Info -> Google Maps URL) feeds two very
// different consumers: an <iframe> that ONLY accepts frameable embed URLs,
// and <a> links that must NOT be given an embed URL (the Embed API errors
// with "must be used in an iframe" when opened as a page). These convert
// whatever the admin pasted into the right form for each.

/** Latitude/longitude pulled out of any Google Maps URL shape, or null. */
const extractLatLng = (url) => {
  // Order matters: a place URL contains BOTH an @lat,lng viewport centre
  // and an !3d/!4d pair for the actual place — the latter is the pin, so
  // it wins. Embed (pb=) URLs use !2d<lng>!3d<lat>, i.e. reversed.
  const place = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
  if (place) return { lat: place[1], lng: place[2] };
  const embed = url.match(/!2d(-?\d+\.\d+)!3d(-?\d+\.\d+)/);
  if (embed) return { lat: embed[2], lng: embed[1] };
  const at = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (at) return { lat: at[1], lng: at[2] };
  const q = url.match(/[?&]q=(-?\d+\.\d+),\s*(-?\d+\.\d+)/);
  if (q) return { lat: q[1], lng: q[2] };
  return null;
};

/**
 * A config-supplied URL, but only when it's safe to put in an href or an
 * iframe src — otherwise ''.
 *
 * Everything in `config` is attacker-reachable in practice (the app writes
 * that node from ordinary visitors' browsers on load — see ConfigContext),
 * and these values land directly in `href`/`src` attributes. A
 * `javascript:` URL there executes in this origin on click, and a
 * `data:text/html` one renders an attacker's page inside the club's site.
 * So the scheme is whitelisted rather than blacklisted: anything that isn't
 * plainly http(s) is dropped, including protocol-relative "//evil.example"
 * and any leading-whitespace/control-character trick used to smuggle a
 * scheme past a looser check.
 *
 * @param {string} url
 * @returns {string} the URL, or '' when it isn't a plain http(s) one
 */
export const safeExternalUrl = (url) => {
  // Strip C0 controls and spaces before testing — browsers ignore them
  // inside a scheme ("java\nscript:alert(1)" still runs), so a regex
  // anchored on the raw string alone is not enough.
  const cleaned = String(url || '').replace(/[\u0000-\u0020]/g, '');
  return /^https?:\/\/[^/]/i.test(cleaned) ? cleaned : '';
};

/**
 * True only for a real Google Maps embed URL. The previous test was an
 * unanchored /\/maps\/embed/ anywhere in the string, which any URL can
 * satisfy just by ending in "#/maps/embed".
 * @param {string} url - already through safeExternalUrl
 * @returns {boolean}
 */
const isGoogleMapsEmbedUrl = (url) =>
  /^https?:\/\/([a-z0-9-]+\.)*google\.[a-z.]+\/maps\/embed/i.test(url);

/**
 * Frameable src for the landing page's embedded map, or null when there's
 * nothing to show. Falls back to a text search of the club's address when
 * the configured URL can't be framed (e.g. a maps.app.goo.gl short link,
 * which carries no coordinates).
 * @param {object} config
 * @returns {string|null}
 */
export const getMapEmbedSrc = (config) => {
  // Anchored to the Google host, not just "contains /maps/embed" anywhere in
  // the string: the old unanchored test passed anything ending in
  // "//maps/embed", which is trivially appended to a hostile URL.
  const url = safeExternalUrl(config?.mapsUrl);
  if (url) {
    if (isGoogleMapsEmbedUrl(url)) return url;
    const coords = extractLatLng(url);
    if (coords) return `https://www.google.com/maps?q=${coords.lat},${coords.lng}&output=embed`;
    if (/google\.[^/]+\/maps/i.test(url)) {
      return `${url}${url.includes('?') ? '&' : '?'}output=embed`;
    }
  }
  return config?.locationText
    ? `https://www.google.com/maps?q=${encodeURIComponent(config.locationText)}&output=embed`
    : null;
};

/**
 * URL for "Get Directions" / map links that open in a new tab. Critically,
 * this never returns an embed URL — an admin who pasted the iframe src
 * would otherwise get Google's "must be used in an iframe" error page, so
 * the coordinates are lifted out of it and turned into a normal Maps link.
 * @param {object} config
 * @returns {string|null}
 */
export const getMapLinkUrl = (config) => {
  // Same scheme whitelist and anchored embed test as getMapEmbedSrc above —
  // this value goes straight into an <a href> on three screens.
  const url = safeExternalUrl(config?.mapsUrl);
  if (url) {
    if (isGoogleMapsEmbedUrl(url)) {
      const coords = extractLatLng(url);
      return coords
        ? `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`
        : (config?.locationText
          ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(config.locationText)}`
          : null);
    }
    return url;
  }
  return config?.locationText
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(config.locationText)}`
    : null;
};

/**
 * Whether a court is currently bookable (Config -> Sports & Courts ->
 * Available). Treats a missing/undefined `available` field as TRUE so
 * configs saved before that field existed — and any court an admin has
 * never touched — stay bookable rather than silently vanishing.
 * @param {object} config - app config
 * @param {number|string} courtNumber
 * @returns {boolean}
 */
export const isCourtAvailable = (config, courtNumber) => {
  const entry = (config?.courts || []).find(c => Number(c.number) === Number(courtNumber));
  return entry?.available !== false;
};

// ---- Shared ownership ----
// Some clubs are co-owned: each court belongs to a different owner, who
// sets their own rate and collects payment into their own GCash/bank
// account. Turned on from Config -> System -> "Shared Ownership".
//
// EVERYTHING below is inert while that switch is off. Every helper here
// short-circuits on isSharedOwnershipEnabled(config) returning false, so a
// club that never ticks the box runs exactly the code path it always did:
// one club-wide QR list, no selection restrictions, no banners.
//
// An owner is { id, name, contactNumber, email, qrCodes: [{id, name, url,
// accName, accNumber}] } and lives in config.owners (Config -> Payment).
// A court points at one via config.courts[].ownerId.

/** @returns {boolean} whether per-court ownership is switched on. */
export const isSharedOwnershipEnabled = (config) => config?.sharedOwnershipEnabled === true;

/**
 * Whether admins may open past dates on the Manage availability grid and act
 * on finished reservations in Booking Management (Config -> System -> "View
 * past dates"; see canActOnBooking in AdminBooking.jsx). Off unless
 * explicitly switched on.
 *
 * @param {object} config - app config
 * @returns {boolean}
 */
export const isPastDateViewEnabled = (config) => config?.adminViewPastDatesEnabled === true;

/**
 * Whether picking Closed on the Manage availability grid asks the admin for a
 * reason first (Config -> System -> "Ask reason for closing timeslot"). On
 * unless explicitly switched off, so older configs keep the prompt.
 *
 * @param {object} config - app config
 * @returns {boolean}
 */
export const isCloseReasonPromptEnabled = (config) => config?.[CLOSE_REASON_PROMPT_CONFIG_KEY] !== false;

/** @returns {Array} the configured owners, always an array. */
export const getOwners = (config) => (Array.isArray(config?.owners) ? config.owners : []);

/**
 * The config.courts entry for a court number, or undefined. Courts are
 * addressed by NUMBER throughout the booking flow (the grid, slotLocks,
 * booking records) while config stores them by id, so every lookup here
 * goes through the number the same way getCourtLabel does.
 */
export const findCourtRecord = (config, courtNumber) =>
  (config?.courts || []).find(c => Number(c.number) === Number(courtNumber));

/**
 * The owner id assigned to a court, or null when none is set. Always null
 * while shared ownership is off, so no caller can accidentally start
 * grouping courts by a stale ownerId left over from a config where the
 * feature was tried and switched back off.
 */
export const getCourtOwnerId = (config, courtNumber) => {
  if (!isSharedOwnershipEnabled(config)) return null;
  const ownerId = findCourtRecord(config, courtNumber)?.ownerId;
  return ownerId ? String(ownerId) : null;
};

/** @returns {object|null} the owner record for an id. */
export const getOwnerById = (config, ownerId) => {
  if (!ownerId) return null;
  return getOwners(config).find(o => String(o.id) === String(ownerId)) || null;
};

/** @returns {object|null} the owner record a court belongs to. */
export const getCourtOwner = (config, courtNumber) =>
  getOwnerById(config, getCourtOwnerId(config, courtNumber));

/** @returns {Array} an owner's usable QR codes (an entry with no image doesn't count). */
export const getOwnerQrCodes = (owner) =>
  (Array.isArray(owner?.qrCodes) ? owner.qrCodes : []).filter(qr => qr && qr.url);

/** Display name for an owner, falling back to something non-blank. */
export const getOwnerLabel = (owner) => (owner?.name || '').trim() || 'Unnamed owner';

/**
 * Why a court can't be added to the current selection, or null when it can.
 *
 * - `unassigned`     — shared ownership is on but this court has no owner,
 *                      so there's no account to pay into.
 * - `owner-no-qr`    — the court has an owner, but that owner never
 *                      uploaded a QR code, so checkout would dead-end.
 * - `different-owner`— the guest already has another owner's court picked.
 *                      One booking is one payment, so it can only ever
 *                      involve one account.
 *
 * `selectedCourtNumbers` must span EVERY date in the selection, not just the
 * date being clicked: a multi-date booking is still a single payment.
 *
 * @param {object} config
 * @param {number|string} courtNumber - the court being added
 * @param {Array<number|string>} selectedCourtNumbers - already picked, all dates
 * @returns {{code: string, message: string, title: string}|null}
 */
export const getOwnershipSelectionBlock = (config, courtNumber, selectedCourtNumbers = []) => {
  if (!isSharedOwnershipEnabled(config)) return null;

  const contact = (config?.contactNumber || '').trim();
  // "message us at 0917..." reads wrong with no number behind it, so the
  // sentence changes shape rather than trailing off into an empty string.
  const reachUs = contact
    ? `Please contact us on our Facebook page or message us at ${contact}.`
    : 'Please contact us on our Facebook page or send us a message.';

  const ownerId = getCourtOwnerId(config, courtNumber);
  if (!ownerId) {
    return {
      code: 'unassigned',
      title: 'Court Unavailable',
      message: `Sorry for the inconvenience. You cannot book this court — it has no owner assigned yet, so we can't take payment for it online. ${reachUs}`,
    };
  }

  const owner = getOwnerById(config, ownerId);
  if (!owner || getOwnerQrCodes(owner).length === 0) {
    return {
      code: 'owner-no-qr',
      title: 'Court Unavailable',
      message: `Sorry for the inconvenience. You cannot book this court online yet — its owner hasn't set up a payment QR code. ${reachUs}`,
    };
  }

  // The first already-selected court that resolves to a different owner.
  // Selections made before the admin switched shared ownership on can
  // legitimately have no owner at all; those are skipped rather than
  // treated as a mismatch, so an in-progress selection isn't bricked.
  const clash = selectedCourtNumbers
    .map(n => getCourtOwnerId(config, n))
    .find(id => id && id !== ownerId);
  if (clash) {
    return {
      code: 'different-owner',
      title: 'Cannot Combine These Courts',
      message: 'Courts owned by different accounts cannot be booked together. Please complete this booking first or reserve courts individually.',
    };
  }

  return null;
};

/**
 * Ownership problems an ADMIN needs to fix, for the warning banners on the
 * admin booking screen and the config screen. Returns [] when shared
 * ownership is off, so the banners never appear for a normal club.
 *
 * @param {object} config
 * @returns {Array<{code: string, message: string}>}
 */
export const findOwnershipConfigIssues = (config) => {
  if (!isSharedOwnershipEnabled(config)) return [];
  const issues = [];
  const courts = (config?.courts || []).slice().sort((a, b) => Number(a.number) - Number(b.number));

  const unassigned = courts.filter(c => !c.ownerId);
  if (unassigned.length > 0) {
    const names = unassigned.map(c => getCourtLabel(config, c.number)).join(', ');
    issues.push({
      code: 'court-no-owner',
      message: `${unassigned.length} court${unassigned.length > 1 ? 's have' : ' has'} no owner assigned (${names}). `
        + `Guests cannot book ${unassigned.length > 1 ? 'them' : 'it'} until an owner is set in Config → Sports & Courts.`,
    });
  }

  // A court pointing at an owner that's since been deleted is a different
  // failure from one that was never assigned, and needs a different fix.
  const orphaned = courts.filter(c => c.ownerId && !getOwnerById(config, c.ownerId));
  if (orphaned.length > 0) {
    const names = orphaned.map(c => getCourtLabel(config, c.number)).join(', ');
    issues.push({
      code: 'court-owner-missing',
      message: `${names} ${orphaned.length > 1 ? 'are' : 'is'} assigned to an owner that no longer exists. `
        + 'Reassign in Config → Sports & Courts.',
    });
  }

  // Only owners that actually have courts pointing at them — an owner
  // added ahead of time with no courts yet isn't blocking anything.
  const ownersInUse = getOwners(config).filter(o => courts.some(c => String(c.ownerId) === String(o.id)));
  const missingQr = ownersInUse.filter(o => getOwnerQrCodes(o).length === 0);
  if (missingQr.length > 0) {
    const names = missingQr.map(getOwnerLabel).join(', ');
    issues.push({
      code: 'owner-no-qr',
      message: `${names} ${missingQr.length > 1 ? 'have' : 'has'} no payment QR code uploaded. `
        + `Guests cannot check out on ${missingQr.length > 1 ? 'their' : 'that owner\'s'} courts until one is added in Config → Payment.`,
    });
  }

  return issues;
};

/**
 * "indoor" / "outdoor" / "add-on" sub-label for a court, or '' when the admin
 * left the type as "Not set" (config.courts[].indoor === null).
 * @param {object} config - app config
 * @param {number|string} courtNumber
 * @returns {string}
 */
export const getCourtEnvLabel = (config, courtNumber) => {
  const entry = (config?.courts || []).find(c => Number(c.number) === Number(courtNumber));
  // Checked first: an add-on is never also indoor/outdoor, and saying which
  // it is matters less than saying it can't be booked on its own.
  if (entry?.isAddOn === true) return 'add-on';
  if (entry?.indoor === true) return 'indoor';
  if (entry?.indoor === false) return 'outdoor';
  return '';
};

// ---- Equipment rental type ----
// Config -> Equipment -> Rental type. A "session" item is charged once for
// the whole reservation however many hours it covers (a paddle the guest
// keeps for the session); an "hourly" item is charged per hour the guest
// actually keeps it, and the guest picks which of their booked hours those
// are (a ball machine they only want for the last hour of a 3-hour block).
//
// Absent on every item saved before this existed, so the getter resolves
// anything unrecognised to 'session' — the behaviour those items already had.

export const EQUIPMENT_RENTAL_TYPES = ['session', 'hourly'];

/** 'session' | 'hourly' for an equipment item, defaulting to 'session'. */
export const getEquipmentRentalType = (item) =>
  (item?.rentalType === 'hourly' ? 'hourly' : 'session');

/** True when this item is priced per booked hour rather than per reservation. */
export const isHourlyEquipment = (item) => getEquipmentRentalType(item) === 'hourly';

/**
 * What one unit of this item costs and on what basis — "₱150/machine per hour"
 * vs "₱100/paddle per session" — so a guest can see WHY the total is what it
 * is before they pick any hours.
 * @param {object} item - a config.equipment entry
 * @returns {string}
 */
export const describeEquipmentPrice = (item, config = null) => {
  const price = Number(item?.price) || 0;
  const unit = item?.unit || 'item';
  return `${formatMoney(price, config)}/${unit} ${isHourlyEquipment(item) ? 'per hour' : 'per session'}`;
};

/**
 * What a rental line costs.
 *
 * Session items are qty × price, once, however many hours or dates the
 * booking spans — the long-standing behaviour. Hourly items multiply by the
 * number of (date, hour) pairs the guest chose to keep the item for, so an
 * unpicked hour is genuinely not charged.
 *
 * @param {object} item - a config.equipment entry
 * @param {number} qty - units rented
 * @param {number} hourCount - chosen (date, hour) pairs; ignored for session items
 * @returns {number}
 */
export const getEquipmentLineTotal = (item, qty, hourCount = 0) => {
  const price = Number(item?.price) || 0;
  const units = Number(qty) || 0;
  if (!isHourlyEquipment(item)) return price * units;
  return price * units * (Number(hourCount) || 0);
};

/**
 * A SAVED rental line as one label — "Ball Machine × 1 · 3 hrs".
 *
 * The hour count is the multiplier an hourly line's subtotal was built from,
 * so without it a ₱450 line under a ₱150/hr item reads as a pricing error to
 * whoever is looking at the receipt. Session lines keep the plain form.
 *
 * @param {object} item - a saved bookingData.equipmentRental.items entry
 * @returns {string}
 */
export const describeEquipmentLine = (item) => {
  const base = `${item?.label || 'Item'} × ${Number(item?.qty) || 1}`;
  const hours = Number(item?.hourCount) || 0;
  if (item?.rentalType !== 'hourly' || hours <= 0) return base;
  return `${base} · ${hours} hr${hours === 1 ? '' : 's'}`;
};

// ---- Add-on courts ----
// Config -> Sports & Courts -> Court type = "Add-on". An add-on is a
// court-shaped extra that is only meaningful ALONGSIDE a real court — a ball
// machine, a coaching lane, a second net. It gets its own column on the
// booking grid and its own hourly rate like any court, but an hour on it can
// only be taken when the guest already holds THAT SAME HOUR on one of the
// courts the admin paired it with.
//
// Stored on the court record as:
//   isAddOn: true
//   addOnPairedCourts: number[]   // which real courts it may accompany
//   addOnRequiredMessage: string  // shown when picked with no court that hour

/** The message a guest sees when an add-on is picked with no court that hour. */
// {item} resolves to the add-on's own name and {courts} to the courts it is
// paired with, so the club never has to retype either after renaming a court
// or changing the pairing. Both work in an admin's custom message too.
export const ADDON_MESSAGE_TOKENS = ['{item}', '{courts}'];

// The blank line is deliberate — the modal body renders with
// white-space: pre-line, so this reads as two paragraphs rather than one wall
// of text (see Modal.css's .modal-message).
export const DEFAULT_ADDON_REQUIRED_MESSAGE =
  'In order to book/reserve the {item}, it must be paired with Court reservation. '
  + 'Please select {courts} time slot first, then you can book the {item}.\n\n'
  + 'Training balls to use are also included when you rent/purchase ball machine. (approx. 60 pcs)';

/** True when this court is configured as an add-on rather than a real court. */
export const isAddOnCourt = (config, courtNumber) => {
  const entry = (config?.courts || []).find(c => Number(c.number) === Number(courtNumber));
  return entry?.isAddOn === true;
};

/**
 * The court numbers an add-on is allowed to accompany.
 *
 * An add-on with nothing paired yet falls back to every real court of its own
 * sport rather than to none: a court switched to "Add-on" and saved before the
 * admin ticked any partners would otherwise be unbookable on every hour, which
 * reads as a broken grid rather than as an unfinished setting.
 *
 * @param {object} config - app config
 * @param {number|string} courtNumber - the add-on
 * @returns {number[]} sorted court numbers, or [] when this isn't an add-on
 */
export const getAddOnPairedCourts = (config, courtNumber) => {
  const entry = (config?.courts || []).find(c => Number(c.number) === Number(courtNumber));
  if (entry?.isAddOn !== true) return [];

  const paired = (Array.isArray(entry.addOnPairedCourts) ? entry.addOnPairedCourts : [])
    .map(Number)
    .filter(Number.isFinite);
  if (paired.length > 0) return [...new Set(paired)].sort((a, b) => a - b);

  return (config?.courts || [])
    .filter(c => c.isAddOn !== true && (!entry.sportId || c.sportId === entry.sportId))
    .map(c => Number(c.number))
    .filter(Number.isFinite)
    .sort((a, b) => a - b);
};

/**
 * The add-on's paired courts as a readable list — "Court 1", "Court 1 or
 * Court 2", "Center Court, Court 2 or Court 3". Naming them is the whole
 * point of the refusal message: "book a court first" leaves the guest to
 * guess WHICH court unlocks the ball machine.
 * @param {object} config - app config
 * @param {number|string} courtNumber - the add-on
 * @returns {string}
 */
export const describeAddOnPairedCourts = (config, courtNumber) => {
  const names = getAddOnPairedCourts(config, courtNumber).map(n => getCourtLabel(config, n));
  if (names.length === 0) return 'a court';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
};

/**
 * The club's own wording for "you need a court first", falling back to the
 * default sentence so an admin who left the box empty still gets a full
 * explanation rather than a bare refusal.
 *
 * {item} and {courts} are substituted in either case, so the message keeps
 * naming the right courts after a rename or a pairing change instead of going
 * stale the way hand-typed court names would.
 *
 * @param {object} config - app config
 * @param {number|string} courtNumber - the add-on
 * @returns {string}
 */
export const getAddOnRequiredMessage = (config, courtNumber) => {
  const entry = (config?.courts || []).find(c => Number(c.number) === Number(courtNumber));
  const template = (entry?.addOnRequiredMessage || '').trim() || DEFAULT_ADDON_REQUIRED_MESSAGE;
  // Regex replace rather than replaceAll: this ships through the app's build
  // to whatever browsers the club's guests actually use, and a plain global
  // regex needs no polyfill on any of them.
  return template
    .replace(/\{item\}/g, getCourtLabel(config, courtNumber))
    .replace(/\{courts\}/g, describeAddOnPairedCourts(config, courtNumber));
};

/**
 * Why an add-on can't be added to this hour, or null when it can.
 *
 * `selectedCourtsForSlot` is the courts already picked for THE SAME date and
 * THE SAME time slot — a court booked at 5PM does not unlock the ball machine
 * at 6PM, since the guest wouldn't be on court to use it.
 *
 * Returns the same {code, title, message} shape as
 * getOwnershipSelectionBlock, so callers handle both refusals identically.
 *
 * @param {object} config - app config
 * @param {number|string} courtNumber - the court being added
 * @param {Array<number|string>} selectedCourtsForSlot - already picked, this date + hour
 * @returns {{code: string, title: string, message: string}|null}
 */
export const getAddOnSelectionBlock = (config, courtNumber, selectedCourtsForSlot = []) => {
  if (!isAddOnCourt(config, courtNumber)) return null;

  const picked = (selectedCourtsForSlot || []).map(Number);
  const paired = getAddOnPairedCourts(config, courtNumber);
  if (paired.some(court => picked.includes(court))) return null;

  return {
    code: 'addon-needs-court',
    title: `${getCourtLabel(config, courtNumber)} Needs a Court`,
    message: getAddOnRequiredMessage(config, courtNumber),
  };
};

/**
 * Add-ons in one hour's selection that have lost every paired court — e.g.
 * the guest dropped the court hour their ball machine was riding on. Callers
 * strip these rather than letting an add-on with nothing to attach to reach
 * checkout as a billable line.
 *
 * One pass is enough: an add-on never pairs with another add-on, so removing
 * these can't orphan anything else.
 *
 * @param {object} config - app config
 * @param {Array<number|string>} selectedCourtsForSlot - one date + hour's courts
 * @returns {number[]}
 */
export const getOrphanedAddOnCourts = (config, selectedCourtsForSlot = []) => {
  const picked = (selectedCourtsForSlot || []).map(Number);
  return picked.filter(court => (
    isAddOnCourt(config, court) &&
    !getAddOnPairedCourts(config, court).some(partner => picked.includes(partner))
  ));
};

// Returns the ₱/hr rate for a given court + time slot, preferring a
// per-court day/night override and falling back to that court's sport's
// peak/off-peak rate (Config -> Sports & Courts).
//
// `sportId` is optional — when omitted this resolves the court's sport by
// looking it up in config.courts (works fine for a single-sport club,
// where every court belongs to the same sport anyway), then falls back to
// config.sports[0] and finally the legacy flat pricePeak/priceOffPeak/
// peakRateStart fields if config.courts/sports haven't been populated yet
// (a config saved before the Sports & Courts migration ran).
// ---- Rate bands ----
// A club's day is divided into named price bands (Config -> Sports & Courts),
// e.g. Off-peak 6AM-3PM ₱300, Mid-peak 3PM-5PM ₱400, Peak 5PM-6AM ₱450.
//
// Each band is { id, name, startHour, endHour, price } with startHour
// INCLUSIVE and endHour EXCLUSIVE, both 0-23 in 24-hour time. A band whose
// endHour is less than or equal to its startHour WRAPS past midnight, so
// 17 -> 6 covers 5PM through 5:59AM. That's what lets a club price one
// continuous evening/overnight block as a single band.
//
// This replaces the original two-rate model (peakRateStart + priceOffPeak +
// pricePeak). Those fields are still read as a fallback so a config saved
// before bands existed keeps pricing exactly as it did — see
// synthesizeLegacyBands below.

/**
 * Days of the week, indexed to match JavaScript's Date.getDay() — 0 is
 * Sunday. A rate band stores the days it applies to as an array of these
 * indexes; an empty or missing array means EVERY day, which is what keeps
 * every band saved before day-scoping existed working unchanged.
 */
export const WEEKDAYS = [
  { index: 0, short: 'Sun', label: 'Sunday' },
  { index: 1, short: 'Mon', label: 'Monday' },
  { index: 2, short: 'Tue', label: 'Tuesday' },
  { index: 3, short: 'Wed', label: 'Wednesday' },
  { index: 4, short: 'Thu', label: 'Thursday' },
  { index: 5, short: 'Fri', label: 'Friday' },
  { index: 6, short: 'Sat', label: 'Saturday' },
];

/**
 * Weekday index for a 'yyyy-MM-dd' date string.
 *
 * Parsed field-by-field rather than through `new Date(str)`, which treats a
 * bare date as UTC midnight and therefore reports the PREVIOUS day for any
 * timezone ahead of UTC — Manila included. Everything else in this app
 * stores dates as plain local 'yyyy-MM-dd', so this has to read them the
 * same way.
 *
 * @param {string} dateStr
 * @returns {number|null} 0-6, or null if it isn't a parseable date
 */
export const getWeekdayFromDateStr = (dateStr) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr || '').trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d.getDay();
};

/**
 * Whether a band applies on a given weekday. A band with no `days` (or an
 * empty one) applies every day.
 * @param {object} band
 * @param {number|null} weekday 0-6, or null to skip the check entirely
 * @returns {boolean}
 */
export const bandAppliesOnDay = (band, weekday) => {
  if (weekday === null || weekday === undefined) return true;
  const days = band?.days;
  if (!Array.isArray(days) || days.length === 0) return true;
  return days.map(Number).includes(Number(weekday));
};

/**
 * "Every day", "Weekends", "Mon, Wed, Fri" — a short human description of a
 * band's day scope, for the admin editor and the guest-facing rate lists.
 * @param {object} band
 * @returns {string}
 */
export const describeBandDays = (band) => {
  const days = Array.isArray(band?.days) ? [...new Set(band.days.map(Number))].sort((a, b) => a - b) : [];
  if (days.length === 0 || days.length === 7) return 'Every day';
  if (days.length === 5 && [1, 2, 3, 4, 5].every(d => days.includes(d))) return 'Weekdays';
  if (days.length === 2 && days.includes(0) && days.includes(6)) return 'Weekends';
  return days.map(d => WEEKDAYS[d]?.short).filter(Boolean).join(', ');
};

// ---- Time-slot sets (Config -> Sports & Courts -> Time Slots) -------------
//
// A club's opening hours are not always the same every day — the common case
// being a venue that closes earlier at weekends. So the configured day is:
//
//   config.timeSlots      the DEFAULT hours, used for any day no set claims
//   config.timeSlotSets   [{ id, label, days: [0,6], slots: [...] }]
//
// `config.timeSlots` deliberately keeps the exact meaning it always had. A
// deployment that never adds a set behaves as it always did, with no
// migration, and any reader that has not been taught about sets still gets a
// sensible list rather than nothing.
//
// Days follow the same 0-6 convention (0 = Sunday) and the same `days` array
// shape as a sport's rate bands, so the two read alike in the config screen.

/**
 * The FIRST set covering `dateStr`, or null when none claims that weekday.
 *
 * Kept for the callers that only need a set's NAME for a label. It is not
 * how hours are resolved any more: a set is scoped to courts as well as
 * days, so several can legitimately claim one weekday and the right answer
 * depends on which court is asking. Use getTimeSlotsForCourt for that.
 *
 * @param {object} config
 * @param {string} dateStr - "yyyy-MM-dd"
 * @returns {object|null}
 */
export const getTimeSlotSetForDate = (config, dateStr) => (
  getTimeSlotSetsForDate(config, dateStr)[0] || null
);

/**
 * The court numbers a set applies to, or null meaning "every court".
 *
 * Null, not an empty array, because those two have to stay
 * distinguishable and Firebase cannot store the difference: it drops an
 * empty array on write, so `courts: []` reads back as absent. Absent
 * therefore has to mean the safe thing — every court — which is also what
 * every set saved before courts existed means.
 *
 * @param {object} set
 * @returns {number[]|null}
 */
export const getSlotSetCourts = (set) => {
  const list = (Array.isArray(set?.courts) ? set.courts : [])
    .map(Number)
    .filter(Number.isFinite);
  return list.length > 0 ? list : null;
};

/**
 * EVERY set claiming `dateStr`'s weekday, in configured order.
 *
 * Plural because a set is scoped to courts as well as days: "Sat, courts
 * 1-2, 6AM-10PM" and "Sat, court 3, 8AM-5PM" are two sets that both apply
 * to Saturday and do not conflict. Which of them governs a given court is
 * getTimeSlotsForCourt's job.
 *
 * @param {object} config
 * @param {string} dateStr - "yyyy-MM-dd"
 * @returns {object[]}
 */
export const getTimeSlotSetsForDate = (config, dateStr) => {
  const weekday = getWeekdayFromDateStr(dateStr);
  if (weekday === null) return [];
  return (config?.timeSlotSets || []).filter(set => (
    Array.isArray(set?.days) && set.days.map(Number).includes(Number(weekday))
  ));
};

/**
 * The hours ONE court is open on one date.
 *
 * Resolution: every set that claims this weekday AND covers this court
 * wins, and their hours are unioned. A court no set covers falls back to
 * the default `config.timeSlots`, which is what leaves a club that has
 * never scoped a set to courts behaving exactly as it always did — an
 * unscoped set covers every court, so no court ever reaches that fallback.
 *
 * An EMPTY array is a real answer: a set with days and no hours is how the
 * club closes that court on those days.
 *
 * @param {object} config
 * @param {string} dateStr - "yyyy-MM-dd"
 * @param {number|string} courtNumber
 * @returns {string[]}
 */
export const getTimeSlotsForCourt = (config, dateStr, courtNumber) => {
  const sets = getTimeSlotSetsForDate(config, dateStr).filter((set) => {
    const courts = getSlotSetCourts(set);
    return courts === null || courts.includes(Number(courtNumber));
  });
  if (sets.length === 0) return sortTimeSlots(config?.timeSlots || []);
  return sortTimeSlots([...new Set(sets.flatMap(set => set.slots || []))]);
};

/**
 * The bookable hours for one date, in chronological order.
 *
 * An EMPTY array is a real answer, not a missing one: a set with days but no
 * hours is how the club says "we are closed that day". Callers should render
 * a closed day rather than falling back to the default hours.
 *
 * @param {object} config
 * @param {string} dateStr - "yyyy-MM-dd"; a missing/unparseable date falls
 *   back to the default hours, since no weekday can be resolved from it
 * @returns {string[]}
 */
export const getTimeSlotsForDate = (config, dateStr, courtNumbers = null) => {
  const sets = getTimeSlotSetsForDate(config, dateStr);
  if (sets.length === 0) return sortTimeSlots(config?.timeSlots || []);

  const courts = (Array.isArray(courtNumbers) ? courtNumbers : (config?.courts || []).map(c => c?.number))
    .map(Number)
    .filter(Number.isFinite);

  // Nothing to union across — a config with no courts yet, or a caller that
  // passed an empty list. Fall back to the sets' own hours so the day still
  // renders the rows it always did.
  if (courts.length === 0) {
    return sortTimeSlots([...new Set(sets.flatMap(set => set.slots || []))]);
  }

  // The union, not the longest single court's list: an hour that only one
  // court is open for still has to have a row, or it cannot be booked at
  // all. Courts missing that hour render it as CLOSED instead.
  return sortTimeSlots([...new Set(
    courts.flatMap(court => getTimeSlotsForCourt(config, dateStr, court)),
  )]);
};

/**
 * 'yyyy-MM-dd' for the day after `dateStr`.
 *
 * Built field by field rather than with `new Date(dateStr)`, which reads a
 * bare date as UTC midnight and lands on the wrong day west of Greenwich.
 * Duplicated from openPlayUtil's version on purpose: that module imports THIS
 * one, so importing it back would close a cycle and leave whichever loaded
 * second holding undefined helpers.
 *
 * @param {string} dateStr
 * @returns {string|null}
 */
const shiftDayStr = (dateStr, delta) => {
  const [y, m, d] = String(dateStr || '').split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d + delta);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};
const nextDayStr = (dateStr) => shiftDayStr(dateStr, 1);
const prevDayStr = (dateStr) => shiftDayStr(dateStr, -1);

/**
 * How far past midnight the grids run, as an hour 0-12.
 *
 * Clamped rather than trusted: this comes from a Config number field, and an
 * out-of-range value would either silently disable the extension or try to
 * append most of the next day to every grid.
 *
 * @param {object} config
 * @returns {number}
 */
export const getNextDayGridEndHour = (config) => {
  const raw = config?.nextDayGridEndHour;
  const hour = Number(raw);
  if (!Number.isFinite(hour)) return DEFAULT_NEXT_DAY_GRID_END_HOUR;
  return Math.min(MAX_NEXT_DAY_GRID_END_HOUR, Math.max(0, Math.round(hour)));
};

/**
 * The rows a booking grid shows for one date, including the early hours of
 * the NEXT date for a club that trades past midnight.
 *
 * Each row carries the date it actually belongs to, which is the whole point:
 * a 1-2AM row rendered under Friday is Saturday's cell, and every read and
 * write for it — bookings, slotOverrides, slot locks, rates — has to use
 * Saturday's date or it will collide with a different hour entirely.
 *
 *   [{ slot: '11-12AM', dateStr: '2026-09-11', dayOffset: 0 },
 *    { slot: '12-1AM',  dateStr: '2026-09-12', dayOffset: 1 }, ...]
 *
 * The next-day tail is CONTIGUOUS-ONLY, matching AdminHostOpenPlayModal's
 * overnight timeline: it extends while each hour follows the last without a
 * break, and stops at the first gap. A club whose day ends at 11PM and has no
 * 12-1AM configured gets exactly the grid it had before — the extension can
 * only ever continue a night that is already running, never invent one.
 *
 * @param {object} config
 * @param {string} dateStr - 'yyyy-MM-dd'
 * @param {number[]|null} courtNumbers - as getTimeSlotsForDate
 * @returns {Array<{slot: string, dateStr: string, dayOffset: number}>}
 */
const buildNextDayTail = (config, dateStr, courtNumbers) => {
  const endHour = getNextDayGridEndHour(config);
  const own = getTimeSlotsForDate(config, dateStr, courtNumbers);
  const nextDate = nextDayStr(dateStr);
  if (endHour <= 0 || !nextDate || own.length === 0) return [];

  // Anchored to the day's LAST hour on a 0-47 timeline (25 is 1AM tomorrow),
  // so "does this follow the previous hour?" stays one comparison either side
  // of midnight.
  let lastAbs = parseTimeSlotStartHour(own[own.length - 1]);
  const tail = [];
  getTimeSlotsForDate(config, nextDate, courtNumbers)
    .map(slot => ({ slot, hour: parseTimeSlotStartHour(slot) }))
    .filter(({ hour }) => Number.isFinite(hour) && hour < endHour)
    .sort((a, b) => a.hour - b.hour)
    .forEach(({ slot, hour }) => {
      if (hour + 24 !== lastAbs + 1) return;
      tail.push({ slot, dateStr: nextDate, dayOffset: 1 });
      lastAbs = hour + 24;
    });
  return tail;
};

export const getGridRowsForDate = (config, dateStr, courtNumbers = null) => {
  // Hours of THIS date that the PREVIOUS day's grid already shows as its
  // overnight tail. They are dropped here so each hour has exactly one row in
  // the app: 1AM Wednesday belongs to Tuesday night, and showing it again at
  // the top of Wednesday would be the same bookable cell in two places, with
  // two different dates printed against it.
  const shownByPreviousDay = new Set(
    buildNextDayTail(config, prevDayStr(dateStr), courtNumbers).map(row => row.slot),
  );

  const own = getTimeSlotsForDate(config, dateStr, courtNumbers)
    .filter(slot => !shownByPreviousDay.has(slot))
    .map(slot => ({ slot, dateStr, dayOffset: 0 }));

  return [...own, ...buildNextDayTail(config, dateStr, courtNumbers)];
};

/**
 * Every hour configured ANYWHERE — the default list plus every set's.
 *
 * For the places that need the vocabulary rather than one day's opening
 * hours: mapping a package's hour onto a slot label, or offering a filter
 * across dates. Using one day's list there would hide an hour that exists on
 * another day.
 *
 * @param {object} config
 * @returns {string[]}
 */
export const getAllConfiguredTimeSlots = (config) => sortTimeSlots([...new Set([
  ...(config?.timeSlots || []),
  ...(config?.timeSlotSets || []).flatMap(set => set?.slots || []),
])]);

/**
 * "Weekends", "Mon, Wed, Fri", or "No days selected" — a set's day scope.
 *
 * Unlike a rate band, a set with no days is INERT rather than universal: the
 * default hours are what apply everywhere, and a set exists only to override
 * particular days. Saying "Every day" for an empty selection would be the
 * exact opposite of what it does.
 *
 * @param {object} set
 * @returns {string}
 */
export const describeSlotSetDays = (set) => (
  Array.isArray(set?.days) && set.days.length > 0 ? describeBandDays(set) : 'No days selected'
);

/**
 * Whether `hour` falls inside a band, honouring midnight wrap-around.
 * @param {{startHour: number, endHour: number}} band
 * @param {number} hour 0-23
 * @returns {boolean}
 */
export const isHourInBand = (band, hour) => {
  const start = Number(band?.startHour);
  const end = Number(band?.endHour);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return false;
  // A band covering the whole day (start === end) matches every hour.
  if (start === end) return true;
  return start < end
    ? (hour >= start && hour < end)      // normal band, e.g. 6 -> 15
    : (hour >= start || hour < end);     // wraps midnight, e.g. 17 -> 6
};

/**
 * Turns the legacy two-rate fields into the equivalent pair of bands, so a
 * config that predates this feature prices identically. The old rule was
 * `hour >= peakRateStart ? pricePeak : priceOffPeak`, which is exactly an
 * off-peak band of [0, peakRateStart) plus a peak band of [peakRateStart, 0)
 * wrapping to midnight.
 * @param {object} sport
 * @param {object} config
 * @returns {Array<object>}
 */
const synthesizeLegacyBands = (sport, config) => {
  const split = Number(sport?.peakRateStart ?? config?.peakRateStart);
  const offPeak = Number(sport?.priceOffPeak ?? config?.priceOffPeak) || 0;
  const peak = Number(sport?.pricePeak ?? config?.pricePeak) || 0;
  if (!Number.isFinite(split)) return [];
  // peakRateStart of 0 meant "peak all day" under the old `hour >= split`
  // rule, and the off-peak band [0, 0) would be empty. Emitting it anyway
  // would collide with the start === end "covers the whole day" convention
  // below and make everything off-peak, so it's dropped.
  if (split === 0) {
    return [{ id: 'legacy-peak', name: 'Peak', startHour: 0, endHour: 0, price: peak }];
  }
  return [
    { id: 'legacy-offpeak', name: 'Off-peak', startHour: 0, endHour: split, price: offPeak },
    { id: 'legacy-peak', name: 'Peak', startHour: split, endHour: 0, price: peak },
  ];
};

/**
 * The rate bands in effect for a sport, newest model first and the legacy
 * two-rate fields as a fallback. Always returns an array; an empty one means
 * the club has no usable pricing configured at all.
 * @param {object} config
 * @param {string|null} sportId
 * @returns {Array<object>}
 */
export const getSportRateBands = (config, sportId = null, dateStr = null) => {
  const sport = (config?.sports || []).find(s => s.id === sportId) || config?.sports?.[0];
  const bands = (sport?.rateBands || []).filter(b => b && Number.isFinite(Number(b.startHour)));
  const all = bands.length > 0 ? bands : synthesizeLegacyBands(sport, config);
  // Without a date this returns every band — that's what the screens which
  // LIST rates (landing page, sport picker, FAQ) want. Pass a date and it
  // narrows to the bands actually in effect that weekday, which is what
  // pricing needs.
  const weekday = dateStr ? getWeekdayFromDateStr(dateStr) : null;
  return weekday === null ? all : all.filter(b => bandAppliesOnDay(b, weekday));
};

/**
 * The band that applies to a time-slot label, or null when nothing covers it
 * (an admin left a gap in the day). Slots are one hour, so the slot's START
 * hour alone decides the band.
 * @param {object} config
 * @param {string} timeSlot e.g. "6-7AM"
 * @param {string|null} sportId
 * @returns {object|null}
 */
export const getRateBandForSlot = (config, timeSlot, sportId = null, dateStr = null) => {
  const hour = parseTimeSlotStartHour(timeSlot);
  const bands = getSportRateBands(config, sportId, dateStr);
  return bands.find(b => isHourInBand(b, hour)) || null;
};

/**
 * A court's price for one band: its own override if it has set one for that
 * band, otherwise the band's club-wide price.
 *
 * An override of '' / null / undefined means "inherit" — only an explicit
 * number counts, so a court priced at 0 (free) is still honoured.
 *
 * @param {object|null} courtRecord entry from config.courts
 * @param {object|null} band
 * @returns {number}
 */
export const getCourtBandPrice = (courtRecord, band) => {
  const bandPrice = Number(band?.price) || 0;
  const override = courtRecord?.bandRates?.[band?.id];
  if (override === undefined || override === null || override === '') return bandPrice;
  const n = Number(override);
  return Number.isFinite(n) ? n : bandPrice;
};

/**
 * Every band that applies to one court, with that court's effective price for
 * each. Used by the screens that PRESENT rates (the booking grid's rate strip,
 * the sport picker's "from ₱x", the landing page Rates section) so they all
 * agree with what checkout actually charges.
 * @param {object} config
 * @param {number|string} court
 * @param {string|null} sportId
 * @returns {Array<{band: object, price: number}>}
 */
const getCourtRateBandsRaw = (config, court, sportId = null, dateStr = null) => {
  const courtRecord = (config?.courts || []).find(
    c => Number(c.number) === Number(court) && (!sportId || c.sportId === sportId)
  ) || null;
  const resolvedSportId = sportId || courtRecord?.sportId || null;
  return getSportRateBands(config, resolvedSportId, dateStr).map(band => ({
    band,
    price: getCourtBandPrice(courtRecord, band),
  }));
};

/**
 * What a promo charges across a whole band, or the band price when it can't
 * be said in one number.
 *
 * A band is a range of hours but the rates strip prints ONE price for it, and
 * a promo can be scoped to fewer hours than the band covers. So the promo
 * price is only used when every hour in the band agrees on it; a partial
 * promo leaves the band showing its normal rate, and the grid cells — which
 * are per hour — carry the real prices.
 *
 * @param {object} config
 * @param {object} target - { court, band, sportId, dateStr, basePrice }
 * @returns {number}
 */
const promoPriceForBand = (config, { court, band, sportId, dateStr, basePrice }) => {
  if (!dateStr || !band?.id) return basePrice;
  const hours = getAllConfiguredTimeSlots(config)
    .filter(slot => getRateBandForSlot(config, slot, sportId, dateStr)?.id === band.id);
  if (hours.length === 0) return basePrice;

  const prices = hours.map(slot => applyPromoToRate(config, {
    court, timeSlot: slot, sportId, dateStr, baseRate: basePrice,
  }));
  return prices.every(price => price === prices[0]) ? prices[0] : basePrice;
};

/**
 * Every rate band that applies to a court, with that court's price for each —
 * PROMOS INCLUDED, so the rates strip above the guest grid quotes the same
 * money the cells and checkout do.
 *
 * @param {object} config
 * @param {number|string} court
 * @param {string|null} sportId
 * @param {string|null} dateStr
 * @returns {Array<{band: object, price: number}>}
 */
export const getCourtRateBands = (config, court, sportId = null, dateStr = null) => {
  const courtRecord = (config?.courts || []).find(
    c => Number(c.number) === Number(court) && (!sportId || c.sportId === sportId)
  ) || null;
  const resolvedSportId = sportId || courtRecord?.sportId || null;
  return getCourtRateBandsRaw(config, court, sportId, dateStr).map(({ band, price }) => ({
    band,
    price: promoPriceForBand(config, {
      court, band, sportId: resolvedSportId, dateStr, basePrice: price,
    }),
  }));
};

/**
 * The hourly price for one court in one time slot — the single source of
 * truth for every total in the app (grid, checkout, receipt, admin).
 *
 * Resolution order: the court's own override for the matching band, then the
 * band's price, then (for a config with no bands at all) the original
 * peak/off-peak fields including the legacy flat `courtRates` array.
 *
 * @param {object} config
 * @param {number|string} court
 * @param {string} timeSlot
 * @param {string|null} sportId
 * @param {string|null} dateStr 'yyyy-MM-dd' — required for day-scoped bands
 *   to resolve correctly; omit only where no date is in scope.
 * @returns {number}
 */
export const getCourtRate = (config, court, timeSlot, sportId = null, dateStr = null) => {
  const courtRecord = (config?.courts || []).find(
    c => Number(c.number) === Number(court) && (!sportId || c.sportId === sportId)
  );
  const resolvedSportId = sportId || courtRecord?.sportId || null;
  const base = resolveBandRate(config, court, timeSlot, sportId, dateStr);

  // A live promo (Admin -> Promo) replaces the band price for the hours it
  // covers. Applied HERE, at the one function every total in the app already
  // goes through, so the grid, checkout, receipt, admin screens and export
  // agree without any of them knowing promos exist.
  //
  // Without a date nothing can apply: whether a promo is running is a
  // question about a day, and callers that pass no date are asking about
  // pricing in the abstract.
  return applyPromoToRate(config, {
    court, timeSlot, sportId: resolvedSportId, dateStr, baseRate: base,
  });
};

/** getCourtRate's original body: the rate bands alone, no promo. */
const resolveBandRate = (config, court, timeSlot, sportId = null, dateStr = null) => {
  const courtRecord = (config?.courts || []).find(
    c => Number(c.number) === Number(court) && (!sportId || c.sportId === sportId)
  );
  const resolvedSportId = sportId || courtRecord?.sportId || null;
  const band = getRateBandForSlot(config, timeSlot, resolvedSportId, dateStr);

  if (band) {
    // A court that predates per-band overrides may still carry the old
    // dayRate/nightRate pair. Honour it against the synthesized legacy bands
    // so its pricing doesn't silently revert to the club-wide rate.
    if (!courtRecord?.bandRates && band.id?.startsWith('legacy-')) {
      const legacy = courtRecord || (config?.courtRates || []).find(r => Number(r.court) === Number(court));
      const raw = band.id === 'legacy-peak' ? legacy?.nightRate : legacy?.dayRate;
      if (raw !== undefined && raw !== null && raw !== '') return Number(raw) || 0;
    }
    return getCourtBandPrice(courtRecord, band);
  }

  // No band covers this hour (a gap in the configured day). Fall back to the
  // cheapest configured band rather than charging nothing, and let the admin
  // UI surface the gap.
  // Raw, not the promo-aware list: this value is about to be handed to
  // applyPromoToRate by getCourtRate, and a promo baked in here would be
  // discounted a second time.
  const all = getCourtRateBandsRaw(config, court, resolvedSportId, dateStr).map(b => b.price).filter(n => Number.isFinite(n));
  return all.length ? Math.min(...all) : 0;
};

/**
 * The band price for one court-hour, BEFORE any promo.
 *
 * Kept separate so the admin screens can still show what a court normally
 * costs — a promo card that read the discounted price back as its own "before"
 * figure would show 20% off 20% off.
 *
 * @param {object} config
 * @param {number|string} court
 * @param {string} timeSlot
 * @param {string|null} sportId
 * @param {string|null} dateStr
 * @returns {number}
 */
export const getBaseCourtRate = (config, court, timeSlot, sportId = null, dateStr = null) =>
  resolveBandRate(config, court, timeSlot, sportId, dateStr);

// ---- Processing fee ----
// A club-wide surcharge on top of court time (Config -> Pricing). It is
// charged flat — once per reservation — unless "Charge per hour" is ticked,
// in which case it multiplies by every court-hour booked.
//
// NAMING: guests and admins see "processing fee", but the field is stored as
// `bookingFee` on every booking record and on the config. The two are
// deliberately out of step — renaming the field would orphan the amount on
// every reservation already in Firebase, so the wording changed and the
// schema did not.

/**
 * How many court-hours a reservation covers: one unit per (date, hour,
 * court) cell. Two courts for three hours is SIX, and a multi-date booking
 * counts every date.
 *
 * Accepts either shape the app carries a selection in:
 *   - the guest's in-progress picks, { '2026-08-20': { '8-9AM': [1, 2] } }
 *   - a created booking's `dates`, [{ date, timeSlots: { '8-9AM': [1, 2] } }]
 *   - a single booking record's bare `timeSlots` object
 *
 * Firebase strips empty arrays and stores a single-element list as an object
 * in some paths, so a cell may come back as an array, an object or a bare
 * number — all three are counted rather than assumed.
 *
 * @param {object|Array} selection
 * @returns {number} total court-hours, 0 for anything unrecognized
 */
export const countBookedCourtHours = (selection) => {
  if (!selection) return 0;

  const countSlotMap = (slotMap) => Object.values(slotMap || {}).reduce((sum, courts) => {
    if (Array.isArray(courts)) return sum + courts.length;
    if (courts && typeof courts === 'object') return sum + Object.keys(courts).length;
    // A bare value (a lone court number) is still one court-hour.
    return sum + (courts === undefined || courts === null ? 0 : 1);
  }, 0);

  // [{ date, timeSlots }] — a created booking's date list.
  if (Array.isArray(selection)) {
    return selection.reduce((sum, entry) => sum + countSlotMap(entry?.timeSlots), 0);
  }

  const values = Object.values(selection);
  // { dateStr: { timeSlot: [courts] } } — the guest's multi-date picks.
  // Distinguished from a bare timeSlots map by its values being slot maps
  // (objects of arrays) rather than arrays of court numbers.
  const isDateKeyed = values.length > 0 && values.every(
    v => v && typeof v === 'object' && !Array.isArray(v)
      && Object.values(v).every(inner => Array.isArray(inner) || (inner && typeof inner === 'object'))
  );
  if (isDateKeyed) return values.reduce((sum, slotMap) => sum + countSlotMap(slotMap), 0);

  return countSlotMap(selection);
};

/**
 * The processing fee actually charged for a reservation.
 *
 * Flat (the default) ignores `selection` entirely and returns the configured
 * amount, which is exactly what this code did before the per-hour option
 * existed. Per-hour multiplies it by countBookedCourtHours above.
 *
 * @param {object} config
 * @param {object|Array} selection - see countBookedCourtHours
 * @returns {number}
 */
export const calculateBookingFee = (config, selection) => {
  const fee = Number(config?.bookingFee) || 0;
  if (fee <= 0) return 0;
  if (config?.bookingFeePerHour !== true) return fee;
  return fee * countBookedCourtHours(selection);
};

/**
 * The processing fee for a reservation that may have come through the event
 * package flow.
 *
 * An event package is sold as an all-in price for the whole venue — the club
 * has already priced the block, inclusions and all — so the per-reservation
 * surcharge that applies to ordinary hourly bookings is NOT added on top.
 *
 * Every screen that shows or stores the fee goes through here, so the
 * pre-payment summary, the checkout card, the saved record and the PDF
 * receipt can never disagree about whether one was charged.
 *
 * @param {object} config
 * @param {object|Array} selection - see countBookedCourtHours
 * @param {object|null} packageBooking - set when this is a package booking
 * @returns {number}
 */
export const calculateReservationFee = (config, selection, packageBooking = null) =>
  (packageBooking ? 0 : calculateBookingFee(config, selection));

/**
 * "₱5 × 6 hrs" for a receipt line, or '' when the fee is flat (where the
 * amount already tells the whole story and a breakdown would be noise).
 */
export const describeBookingFee = (config, selection) => {
  if (config?.bookingFeePerHour !== true) return '';
  const hours = countBookedCourtHours(selection);
  if (hours <= 0) return '';
  return `${formatMoney(config?.bookingFee, config)} × ${hours} hr${hours === 1 ? '' : 's'}`;
};

// Checks a guest's selected slots against a bookedSlots map
// ({ timeSlot: [{ court, userName, status }] }) and returns any conflicts —
// courts the guest picked that are already actively held by someone else.
// Pure/no Firebase dependency, so it can run instantly against whatever
// bookedSlots state a screen already has (e.g. App.jsx's realtime
// listener), without needing an extra round trip to the database.
export const findConflictsAgainstBookedSlots = (bookedSlotsMap, selectedSlots) => {
  const conflicts = [];
  Object.entries(selectedSlots || {}).forEach(([timeSlot, courts]) => {
    const bookedForSlot = (bookedSlotsMap || {})[timeSlot] || [];
    (courts || []).forEach(court => {
      const hit = bookedForSlot.find(b => b.court === court);
      if (hit) conflicts.push({ timeSlot, court, bookedBy: hit.userName || 'Unknown' });
    });
  });
  return conflicts;
};

// converts 24hr format ie 18 to 6pm, 0 to 12am, 12 to 12pm.
export const convertTo12Hour = (hour24) => {
  if (hour24 < 0 || hour24 > 23 || isNaN(hour24)) {
    throw new Error("Hour must be between 0 and 23");
  }

  if (hour24 === 0) return "12am";
  if (hour24 === 12) return "12pm";

  return hour24 > 12
    ? `${hour24 - 12}pm`
    : `${hour24}am`;
}

// The club logo is shown in the middle of the boot loading ring, but that
// screen (AuthGuard.js) renders OUTSIDE ConfigProvider — it's what blocks
// rendering until auth resolves, before any config has loaded — so it
// can't read config.logoUrl from context. ConfigContext mirrors the logo
// into localStorage under this key whenever it loads, and the spinner
// reads it back from there. A first-ever visit has nothing cached yet and
// simply shows the ring on its own.
const LOGO_CACHE_KEY = 'jt-cached-logo-url';

/** Remembers the club logo for the next boot screen. Safe to call anywhere. */
export const cacheLogoUrl = (url) => {
  try {
    if (url) localStorage.setItem(LOGO_CACHE_KEY, url);
    else localStorage.removeItem(LOGO_CACHE_KEY);
  } catch {
    // Private-browsing / storage-disabled — the logo just won't show on the
    // boot screen, which is purely cosmetic. Never worth breaking boot over.
  }
};

/** The logo cached by a previous session, or '' on a first visit. */
export const getCachedLogoUrl = () => {
  try {
    return localStorage.getItem(LOGO_CACHE_KEY) || '';
  } catch {
    return '';
  }
};

// Just the spinner ring + label — a 12-dot rotating ring (see jt-spinner-*
// rules in global.css) matching the jassen theme's cream background /
// teal dot gradient, with no full-page wrapper. Use this to embed a loading
// state inline (e.g. inside a modal); use loadingSpinner() below for a
// full-page loading screen.
// The club logo renders centred inside the ring on EVERY loading state.
// `logoUrl` is optional and only needs passing when a caller has a fresher
// value than the cache; left out, it falls back to the cached logo, which
// ConfigContext keeps in sync on every config load. That way new loading
// states pick the logo up automatically instead of each caller having to
// remember to thread it through.
// The ring itself is the element that spins and the logo is its sibling,
// so the logo stays upright rather than rotating with the dots.
export const spinnerRing = (message, logoUrl) => {
  const logo = logoUrl || getCachedLogoUrl();
  return (
    <>
      <div className="jt-spinner-wrap">
        <div className="jt-spinner">
          {Array.from({ length: 12 }).map((_, i) => (
            <span key={i} className="jt-spinner-dot" style={{ '--jt-spinner-angle': `${i * 30}deg` }} />
          ))}
        </div>
        {logo && <img src={logo} alt="" className="jt-spinner-logo" />}
      </div>
      {message && <p className="jt-spinner-label">{message}</p>}
    </>
  );
};

// Full-page loading spinner component for async operations — centers
// spinnerRing() in a full-viewport, jassen-themed backdrop. Renders a
// larger ring than the inline variant (see .jt-spinner-screen in
// global.css), with the logo in the middle when one is available.
export const loadingSpinner = (message, logoUrl) => {
  return (
    <div className="jt-spinner-screen">
      {spinnerRing(message, logoUrl)}
    </div>
  );
}

/**
 * Strips an initials entry down to what a grid cell's badge can hold:
 * letters and digits only, upper-cased, capped at MAX_CELL_INITIALS_LENGTH.
 *
 * Shared by the field that captures them (CellInitialsInput) and the save
 * that writes them as a booker name (createAdminBlockBooking), so a value
 * that reached the change list some other way still cannot put punctuation
 * or a third character into a booking record.
 *
 * @param {string} value - Raw field value.
 * @returns {string} Up to MAX_CELL_INITIALS_LENGTH upper-case alphanumerics.
 */
export const sanitizeInitials = (value) =>
  (value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, MAX_CELL_INITIALS_LENGTH);


/**
 * A booking's timeSlots map with specific (hour, court) cells taken out.
 *
 * Lets an admin release ONE hour of a multi-hour reservation instead of
 * cancelling the whole thing: the booking record survives, minus the hours
 * removed. An hour left with no courts on it drops out entirely rather than
 * lingering as an empty array, which would still read as a booked hour to
 * everything that walks this map.
 *
 * Pure — it returns a new map and never touches the one passed in, so a
 * caller can compare before and after.
 *
 * @param {object} timeSlots - { [timeSlot]: number[] | object }, as stored.
 * @param {Array<{timeSlot: string, court: number}>} cells - Cells to remove.
 * @returns {object} A new timeSlots map. Empty when nothing is left.
 */
export const removeSlotsFromBooking = (timeSlots, cells) => {
  const remaining = {};
  // Firebase hands a court list back as an array or, once it has holes, as
  // an object keyed by index — both shapes are read the same way everywhere
  // else in this file, so they are here too.
  Object.entries(timeSlots || {}).forEach(([slot, courts]) => {
    const courtList = Array.isArray(courts) ? courts : Object.values(courts || {});
    const kept = courtList
      .map(Number)
      .filter(court => !(cells || []).some(c => c.timeSlot === slot && Number(c.court) === court));
    if (kept.length > 0) remaining[slot] = kept;
  });
  return remaining;
};

/**
 * The bookable courts of one sport, for a headline count ("2 courts").
 *
 * Add-on columns are left out — they're extras attached to a court hour, not
 * courts a guest can reserve on their own. Falls back to the legacy flat
 * numberOfCourts for a config saved before config.courts existed.
 *
 * @param {object} config - app config
 * @param {string|null} [sportId] - the sport on screen; null counts every sport
 * @returns {number}
 */
export const getSportCourtCount = (config, sportId = null) => {
  const courts = (config?.courts || []).filter(c => (
    (!sportId || c.sportId === sportId) && !isAddOnCourt(config, c.number)
  ));
  if (courts.length > 0) return courts.length;
  return Math.max(0, Number(config?.numberOfCourts) || 0);
};

/**
 * "Open every day", "Open weekdays", "Open Mon, Wed, Fri" — the weekdays the
 * club takes bookings at all, for the guest screen's headline facts.
 *
 * A weekday counts as open when it resolves to at least one time slot through
 * getTimeSlotsForDate, the same resolver the grid draws from — so a weekday a
 * Time Slots set leaves empty reads as closed here exactly as it does there.
 * Each weekday is checked on a real date in the coming week because that
 * resolver takes dates, not weekday numbers.
 *
 * @param {object} config - app config
 * @param {Date} [reference] - where the sample week starts (today by default)
 * @returns {string} '' when no weekday has any hours
 */
export const getOpenDaysLabel = (config, reference = new Date()) => {
  const openDays = [];
  for (let offset = 0; offset < 7; offset += 1) {
    const day = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate() + offset);
    const dateStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
    if (getTimeSlotsForDate(config, dateStr).length > 0) openDays.push(day.getDay());
  }
  if (openDays.length === 0) return '';

  const described = describeBandDays({ days: openDays });
  // The named groups read as prose ("Open weekdays"); a list of short day
  // names keeps its capitals ("Open Mon, Wed").
  const isNamedGroup = ['Every day', 'Weekdays', 'Weekends'].includes(described);
  return `Open ${isNamedGroup ? described.toLowerCase() : described}`;
};
